import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import {
  User,
  Mail,
  Phone,
  Users,
  ArrowRight,
  MapPin,
  Calendar as CalendarIcon,
  ShieldCheck,
  ChevronLeft
} from "lucide-react";
import { toast } from "react-toastify";

function BookingFormPage() {
  const { state } = useLocation(); // accommodation data
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    userName: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    qty: 1,
  });

  useEffect(() => {
    if (!state) {
      toast.error("No accommodation selected");
      navigate("/useraccommodations");
    }
  }, [state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const totalPrice = state.pricePerNight * formData.qty;
    navigate("/payment", { state: { ...formData, accommodation: state, totalPrice } });
  };

  if (!state) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-sky-600 font-bold text-sm mb-8 transition-colors group"
          >
            <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            Back to Details
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left: Form */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-[2.5rem] shadow-xl shadow-sky-900/5 p-10 border border-sky-100/50">
                <div className="mb-10">
                  <h1 className="text-3xl font-black text-sky-950 tracking-tight">Booking Details</h1>
                  <p className="text-gray-500 font-medium mt-2">Personalize your journey at <span className="text-sky-600 font-bold">{state.accommodationName}</span></p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <User size={12} /> Full Name
                      </label>
                      <input
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 placeholder:text-gray-300"
                        placeholder="Dilmith Ranasinghe"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <Mail size={12} /> Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 placeholder:text-gray-300"
                        placeholder="hello@example.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <Phone size={12} /> Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 placeholder:text-gray-300"
                        placeholder="+94 77 123 4567"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <Users size={12} /> Number of Guests
                      </label>
                      <div className="flex items-center bg-gray-50 rounded-2xl px-4 border border-transparent focus-within:bg-white focus-within:ring-4 focus-within:ring-sky-500/10 focus-within:border-sky-400 transition-all">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, qty: Math.max(1, prev.qty - 1) }))}
                          className="w-10 h-10 flex items-center justify-center text-sky-600 hover:bg-sky-100 rounded-xl transition-colors font-bold text-xl"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          name="qty"
                          min="1"
                          value={formData.qty}
                          onChange={handleChange}
                          className="flex-1 bg-transparent border-none text-center font-black text-sky-950 focus:ring-0"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, qty: prev.qty + 1 }))}
                          className="w-10 h-10 flex items-center justify-center text-sky-600 hover:bg-sky-100 rounded-xl transition-colors font-bold text-xl"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-50/50 rounded-3xl p-6 border border-emerald-100 flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/30">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <p className="text-emerald-900 font-black text-sm">Safe & Secure Booking</p>
                      <p className="text-emerald-600/70 text-[11px] font-bold">Your information is encrypted and never shared.</p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-2xl font-black text-lg shadow-xl shadow-sky-500/30 hover:shadow-sky-500/50 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-3 group"
                  >
                    Proceed to Payment
                    <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              </div>
            </div>

            {/* Right: Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-[2.5rem] shadow-xl shadow-sky-900/5 border border-sky-100/50 overflow-hidden sticky top-24">
                <div className="relative h-48">
                  <img
                    src={state.images?.[0] || "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?q=80&w=1000"}
                    alt={state.accommodationName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
                    <span className="text-sky-600 text-[10px] font-black uppercase tracking-widest leading-none block text-center">Price / Night</span>
                    <span className="text-sky-900 text-sm font-black block text-center mt-0.5">LKR {state.pricePerNight?.toLocaleString()}</span>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  <div>
                    <h3 className="text-xl font-black text-sky-950 tracking-tight">{state.accommodationName}</h3>
                    <p className="text-gray-400 text-xs font-bold flex items-center gap-1 mt-1 uppercase tracking-wider">
                      <MapPin size={12} className="text-sky-500" /> Sri Lanka
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                    <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                      <span>Rate Type</span>
                      <span className="text-sky-900">Premium Standard</span>
                    </div>
                    <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                      <span>Selection</span>
                      <span className="text-sky-900">{formData.qty} Guests</span>
                    </div>
                    <div className="pt-4 border-t border-gray-200 flex justify-between items-end">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Final Quote</span>
                      <div className="text-right">
                        <span className="text-3xl font-black text-sky-950 leading-none block">
                          LKR {(state.pricePerNight * formData.qty).toLocaleString()}
                        </span>
                        <span className="text-[10px] font-bold text-emerald-500 mt-1 block">All inclusive</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 px-2">
                    <div className="w-8 h-8 bg-sky-50 rounded-xl flex items-center justify-center text-sky-500">
                      <CalendarIcon size={16} />
                    </div>
                    <p className="text-[11px] font-bold text-gray-400">Confirmation received instantly after successful payment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Fotter />
    </div>
  );
}

export default BookingFormPage;